import React, { useCallback, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import EditItem from "pages/shopManager/EditItem";
import { deleteShop, fetchShops, setShopMainExposure } from "shared/api/ShopApi";
import { applyDateFilter } from "shared/lib/date/dateFilters";
import { toKstDate } from "shared/lib/date/formatKst";
import { ActionButton, EditButton } from "shared/ui/buttons";
import { alertDialog, confirmDialog } from "shared/ui/feedback";
import { SearchPanel } from "shared/ui/filters";
import { AddHeader, AlertDiv, BaseContainer } from "shared/ui/layout";
import {
  DataTable,
  Pagination,
  SelectAllToggle,
  useClientPagination,
  useTableSelection,
} from "shared/ui/table";

const CATEGORY_OPTIONS = ["전체", "식사", "카페", "피트니스", "미용", "기타"];
const STATUS_OPTIONS = ["전체", "진행 중", "종료"];
const QUICK_RANGE_OPTIONS = [
  { label: "오늘", value: 0 },
  { label: "1주일", value: 1 },
  { label: "1개월", value: 2 },
  { label: "3개월", value: 3 },
  { label: "6개월", value: 4 },
  { label: "1년", value: 5 },
];

function ShopManager() {
  const navigate = useNavigate();
  const [ShopData, setShopData] = useState([]);
  const itemsPerPage = 9;
  const [isSearched, setIsSearched] = useState(false);
  const [queriedShopData, setQueriedShopData] = useState([]);
  const [selectedItemId] = useState(null);

  const toDisplayDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString("ko-KR");
  };

  const normalizeShop = useCallback((item) => {
    const pinValue =
      item?.pin ?? item?.isPin ?? item?.mainExposure ?? item?.mainExpose;
    const progressSource =
      item?.inProgress ??
      item?.progress ??
      item?.isProgress ??
      item?.proceeding ??
      item?.status;
    const createdSource =
      item?.createdAt ??
      item?.createAt ??
      item?.createdDate ??
      item?.regDate ??
      item?.registeredAt;

    const normalizedProgress =
      typeof progressSource === "boolean"
        ? progressSource
          ? "진행 중"
          : "종료"
        : progressSource || "-";

    return {
      ...item,
      title: item?.title ?? "",
      pinDisplay:
        typeof pinValue === "boolean" ? (pinValue ? "Yes" : "No") : pinValue || "-",
      progressDisplay: normalizedProgress,
      createdAtDisplay: toDisplayDate(createdSource),
      createdAt: item?.createdAt ?? createdSource,
    };
  }, []);

  const handleButtonClick = () => {
    navigate("/shopManager/shopRegister/");
  };

  const ReadHGUshop = useCallback(async () => {
    try {
      const shops = await fetchShops();

      const normalizedData = (shops || []).map(normalizeShop);
      setShopData(normalizedData);
    } catch (error) {
    }
  }, [normalizeShop]);

  useEffect(() => {
    ReadHGUshop();
  }, [ReadHGUshop]);

  const sourceData = isSearched ? queriedShopData : ShopData;
  const selection = useTableSelection(sourceData);
  const { pageItems, page, setPage, pageSize, totalCount } =
    useClientPagination(sourceData, { pageSize: itemsPerPage });

  const handleSearchFilters = (filters) => {
    const { dateButtonClicked, chips, startDate, endDate, keyword } = filters;
    const selectedCategory = chips?.category;
    const selectedStatus = chips?.status;

    let filteredResults = ShopData;

    const keywordLower = keyword.toLowerCase();

    filteredResults = filteredResults.filter((item) =>
      item.title.toLowerCase().includes(keywordLower),
    );

    if (selectedCategory && selectedCategory !== "전체") {
      filteredResults = filteredResults.filter(
        (item) => item.category === selectedCategory,
      );
    }

    // 구분(진행여부) 필터: 표에 표시되는 progressDisplay 값과 동일하게 비교한다.
    if (selectedStatus && selectedStatus !== "전체") {
      filteredResults = filteredResults.filter(
        (item) => item.progressDisplay === selectedStatus,
      );
    }

    filteredResults = applyDateFilter({
      data: filteredResults,
      dateButtonClicked,
      startDate,
      endDate,
      // createdAt은 서버가 UTC로 생성하므로 KST 보정해서 비교.
      getDate: (item) => toKstDate(item.createdAt) ?? new Date(NaN),
    });

    setQueriedShopData(filteredResults);
    setIsSearched(true);
  };

  const handleResetFilters = () => {
    setQueriedShopData([]);
    setIsSearched(false);
    setPage(1);
  };

  const handleEditClick = useCallback((shopId) => {
    navigate(`/shopManager/editItem/${shopId}`);
  }, [navigate]);

  const columns = React.useMemo(
    () => [
      { Header: "No.", accessor: "id" },
      { Header: "카테고리", accessor: "category" },
      {
        Header: "제휴 상품",
        accessor: "title",
        Cell: ({ value }) => {
          // Truncate the title to a maximum of 32 characters
          const truncatedTitle =
            value.length > 15 ? value.substring(0, 15) + "..." : value;

          return (
            <span title={value} style={{ fontFamily: "monospace" }}>
              {truncatedTitle}
            </span>
          );
        },
      },
      {
        Header: "메인 노출",
        accessor: "pinDisplay",
        Cell: ({ value }) => (
          <ExposeBadge $exposed={value === "Yes"}>{value}</ExposeBadge>
        ),
      },
      { Header: "진행여부", accessor: "progressDisplay" },
      { Header: "등록일", accessor: "createdAtDisplay" },
      { Header: "조회수", accessor: "viewCnt" },

      {
        Header: "설정",
        accessor: "edit",
        Cell: ({ row }) => (
          <EditBtn onClick={() => handleEditClick(row.original.id)}>
            설정
          </EditBtn>
        ),
      },
    ],
    [handleEditClick],
  );

  const calculateNewPostsCount = () => {
    const today = new Date();
    const newPosts = ShopData.filter((item) => {
      const postDate = new Date(item.createdAt);
      return (
        postDate.getDate() === today.getDate() &&
        postDate.getMonth() === today.getMonth() &&
        postDate.getFullYear() === today.getFullYear()
      );
    });
    return newPosts.length;
  };

  const newPostsCount = calculateNewPostsCount();

  const handleDeleteClick = async () => {
    if (selection.count === 0) {
      alertDialog("선택된 항목이 없습니다.");
      return;
    }

    const selectedRowsWithIds = selection.selectedRows.filter(
      (row) => row && row.id,
    );
    const selectedRowsIds = selectedRowsWithIds.map((row) => row.id);
    const confirmDelete = await confirmDialog("선택한 항목을 삭제하시겠습니까?");

    if (confirmDelete) {
      try {
        await Promise.all(
          selectedRowsIds.map(async (id) => {
            await deleteShop(id);
          }),
        );

        // 삭제된 항목을 테이블 데이터에서도 제거
        const updatedTableData = ShopData.filter(
          (item) => !selectedRowsIds.includes(item.id),
        );
        setShopData(updatedTableData);
        setQueriedShopData((prev) =>
          prev.filter((item) => !selectedRowsIds.includes(item.id)),
        );

        selection.clear();
        alertDialog("삭제되었습니다.");
      } catch (error) {
        // console.error("Error deleting rows:", error);
      }
    }
  };

  const handleMainExposureClick = async (expose) => {
    if (selection.count === 0) {
      alertDialog("선택된 항목이 없습니다.");
      return;
    }

    const selectedRowsIds = selection.selectedRows
      .filter((row) => row && row.id)
      .map((row) => row.id);
    const confirmed = await confirmDialog(
      expose
        ? "선택한 항목을 메인에 노출하시겠습니까?"
        : "선택한 항목의 메인 노출을 해제하시겠습니까?",
    );

    if (!confirmed) return;

    try {
      await setShopMainExposure(selectedRowsIds, expose);

      // 서버 반영 후 테이블의 메인 노출 값을 즉시 갱신(pin + 파생된 pinDisplay).
      const idSet = new Set(selectedRowsIds);
      const applyExposure = (item) =>
        idSet.has(item.id)
          ? { ...item, pin: expose, pinDisplay: expose ? "Yes" : "No" }
          : item;
      setShopData((prev) => prev.map(applyExposure));
      setQueriedShopData((prev) => prev.map(applyExposure));

      selection.clear();
      alertDialog(expose ? "메인 노출로 설정되었습니다." : "메인 노출이 해제되었습니다.");
    } catch (error) {
      alertDialog("메인 노출 설정에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
  };

  return (
    <BaseContainer>
      <AddHeader
        pageName="편의정보 - HGU샵 관리"
        onClick={handleButtonClick}
        buttonValue="HGU샵 등록하기"
      />
      <SearchPanel onSearch={handleSearchFilters} onReset={handleResetFilters}>
        <SearchPanel.Chips
          name="category"
          label="카테고리"
          options={CATEGORY_OPTIONS}
          deselectTo="전체"
        />
        <SearchPanel.Keyword label="조건 검색" />
        <SearchPanel.Chips
          name="status"
          label="구분"
          options={STATUS_OPTIONS}
          deselectTo="전체"
        />
        <SearchPanel.QuickRange label="게시 기간" options={QUICK_RANGE_OPTIONS} />
        <SearchPanel.DateRange />
      </SearchPanel>
      <AlertDiv>
        <p>
          [오늘 등록된 새 글 <P>{newPostsCount}건</P>] 검색 결과{" "}
          <P>{totalCount}건</P> 검색 되었습니다.
        </p>
        <ActionGroup>
          <SelectAllToggle
            totalCount={totalCount}
            selectedCount={selection.count}
            allSelected={selection.isAllSelected}
            onToggle={() =>
              selection.isAllSelected ? selection.clear() : selection.selectAll()
            }
          />
          <ActionButton
            onClick={() => handleMainExposureClick(true)}
            disabled={selection.count === 0}
          >
            메인 노출 지정
          </ActionButton>
          <ActionButton
            onClick={() => handleMainExposureClick(false)}
            disabled={selection.count === 0}
          >
            메인 노출 해제
          </ActionButton>
          <ActionButton
            onClick={handleDeleteClick}
            disabled={selection.count === 0}
          >
            삭제
          </ActionButton>
        </ActionGroup>
      </AlertDiv>
      <DataDiv>
        <DataTable
          columns={columns}
          data={pageItems}
          selectable
          selection={selection}
        />
        {selectedItemId && <EditItem id={selectedItemId} />}
      </DataDiv>

      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
      />
    </BaseContainer>
  );
}

export default ShopManager;

const DataDiv = styled.div`
  width: 100%;
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;


const EditBtn = styled(EditButton)`
  width: fit-content;
  height: fit-content;
  padding: 5px 10px;
`;

const P = styled.span`
  color: #0079e0;
  font-weight: bold;
`;

// 메인 노출 여부를 공지사항 '고정' 칩과 동일한 스타일의 배지로 표시한다.
const ExposeBadge = styled.span`
  display: inline-block;
  min-width: 40px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: ${({ $exposed }) => ($exposed ? 600 : 500)};
  color: ${({ $exposed }) => ($exposed ? "#1c6dd0" : "#5f6b7a")};
  background-color: ${({ $exposed }) => ($exposed ? "#e7f1ff" : "#eceff3")};
`;
